/*
 * Environment-aware SEO helpers.
 *
 * Rule of thumb:
 *   • Runtime hostname === "medicalpeptides.ai"                     → INDEX
 *   • Prerender walk (127.0.0.1) with REACT_APP_BACKEND_URL prod    → INDEX
 *   • Everything else (preview, dev, staging, or a prerender build
 *     that was pointed at a non-production backend)                 → NOINDEX
 *
 * This guarantees:
 *   1. Emergent preview host stays out of Google & LLM crawl indexes.
 *   2. The prerendered production snapshot ships with index/follow.
 *   3. A stray prerender run against a non-prod backend produces a
 *      noindex snapshot instead of silently making preview indexable.
 */

export const PROD_HOST = "medicalpeptides.ai";

/**
 * Returns true when this build/runtime should emit indexable SEO metadata.
 * Truth priority:
 *   1. REACT_APP_PROD_BUILD === "1" — explicit prod-build flag (deployer)
 *   2. Runtime browser hostname matches PROD_HOST
 *   3. Prerender walk (127.0.0.1) + REACT_APP_BACKEND_URL points at prod
 *   4. Everything else → noindex
 */
export function shouldIndexEnvironment() {
  if (process.env.REACT_APP_PROD_BUILD === "1") return true;
  if (typeof window !== "undefined" && window.location) {
    const host = window.location.hostname;
    if (host === PROD_HOST) return true;
    if (host === "127.0.0.1" || host === "localhost") {
      const be = process.env.REACT_APP_BACKEND_URL || "";
      return be.includes(PROD_HOST);
    }
    return false;
  }
  const be = process.env.REACT_APP_BACKEND_URL || "";
  return be.includes(PROD_HOST);
}

export function robotsMeta(explicitNoindex = false) {
  if (explicitNoindex) return "noindex, nofollow";
  return shouldIndexEnvironment()
    ? "index, follow, max-image-preview:large, max-snippet:-1"
    : "noindex, nofollow";
}
